import { AdminOrganizations } from '@platform/client';
import { VettingStatus } from '@platform/client/schemas';
import {
  InputDropdownItem,
  FormsInputDropdown,
  FormsCheckBox,
  FormsMultilineInput,
} from '@platform/components';
import { useState, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AdminTableContext } from '../../AdminTable';
import { AdminFormModal, ModalStage } from '../../AdminFormModal';
import { useForm, SubmitHandler } from 'react-hook-form';

type VettingModalProps = {
  onClose: () => void;
  organizationId: number;
  vettingStatus: VettingStatus;
};

type VettingFormData = {
  vettingResult: VettingStatus;
  sendEmail: boolean;
  vettingNotes?: string;
};

export const VettingModal = (props: VettingModalProps) => {
  const { onClose, organizationId, vettingStatus } = props;
  const { pagination } = useContext(AdminTableContext);
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const { control, handleSubmit, watch, formState } = useForm<VettingFormData>({
    defaultValues: {
      vettingResult: undefined,
      sendEmail: false,
      vettingNotes: undefined,
    },
  });
  const sendEmailSelected = watch('sendEmail');
  const currentVettingResult = watch('vettingResult');
  const queryClient = useQueryClient();
  const vettingMutator = AdminOrganizations.useUpdateOrgVetting({
    mutation: {
      onMutate: () => {
        setModalStage('submitting');
      },
      onError: () => {
        setModalStage('error');
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            ...AdminOrganizations.getGetOrganizationsQueryKey({
              page: pagination.pageIndex,
              limit: pagination.pageSize,
            }),
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [
            ...AdminOrganizations.getGetReviewDataQueryKey({
              organization_id: organizationId,
            }),
          ],
        });
        setModalStage('success');
      },
    },
  });
  const onSubmit: SubmitHandler<VettingFormData> = (data) => {
    if (data.vettingResult) {
      vettingMutator.mutate({
        data: {
          organizationId,
          result: data.vettingResult,
          sendEmail: data.vettingResult !== VettingStatus.pending ? data.sendEmail : false,
          vettingNotes: data.vettingNotes ?? null,
        },
      });
    }
  };
  return (
    <AdminFormModal
      modalStage={modalStage}
      title={`Organization Vetting: ${organizationId}`}
      onClose={onClose}
      submitCTA={{ text: 'Update vetting', callback: () => handleSubmit(onSubmit)() }}
    >
      <div className='flex flex-col'>
        <p className='mb-4'>
          <span className='font-semibold'>Current status:</span>{' '}
          {vettingStatus.split('_').join(' ')}
        </p>
        <FormsInputDropdown
          type='default'
          label='New vetting result'
          control={control}
          name='vettingResult'
          items={resultItems}
          placeholder='Select a result'
          rules={{ required: 'Vetting result is required' }}
          error={formState.errors.vettingResult}
          inputDisabled
        />
        {currentVettingResult !== VettingStatus.pending &&
          currentVettingResult !== VettingStatus.signup_incomplete && (
            <FormsCheckBox
              size='sm'
              control={control}
              name='sendEmail'
              description='Notify organization'
            />
          )}
        {currentVettingResult === 'info_requested' && sendEmailSelected && (
          <FormsMultilineInput
            label='Vetting notes'
            control={control}
            name='vettingNotes'
            rules={{ required: 'Notes required to send this notification' }}
            formsError={formState.errors.vettingNotes}
          />
        )}
      </div>
    </AdminFormModal>
  );
};

const resultItems: InputDropdownItem[] = Object.values(VettingStatus).map((status) => ({
  textValue: status,
  text: status.split('_').join(' '),
}));
